// «Estado de la incidencia»: what the team records about an element, and what a check found, in three
// separate parts that never stand in for each other — the work, the validation of the detection and the
// result of the last check. Saving a state records it and nothing else: it starts no analysis and uses no
// page. «Comprobar la corrección» leads to the summary before the analysis, which says what it would look
// at and what it would use.
import { CheckCircledIcon, ClockIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  archiveWork,
  CHOOSABLE_WORK,
  type CheckReason,
  can,
  changeState,
  type Device,
  type ElementRef,
  type FollowEvent,
  type FollowState,
  historyQuery,
  type Me,
  type Person,
  reviewElement,
  type StateChange,
  type StateTarget,
  scansQuery,
  type ValidationStatus,
  type WorkStatus,
} from '../api';
import { announce, Button, buttonClass, Choice, ConfirmButton, Field, Fold, Message } from '../ui';
import { readForm } from '../util';
import { verificationOf } from '../verification';
import { GuideLink } from './manual-parts';
import { useDate } from './scan-parts';

/** Everything that shows a state reads from the same queries; saving one makes them read again. */
function useRefresh(scanId: string, siteId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      // The issues, the pages and the detail of this scan, in every language.
      queryClient.invalidateQueries({ queryKey: ['scans', scanId] }),
      queryClient.invalidateQueries({ queryKey: scansQuery(siteId).queryKey }),
      queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'follow-up'] }),
      queryClient.invalidateQueries({ queryKey: ['overview'] }),
    ]);
}

/** What the person may do about the state of an element. */
export interface Rights {
  /** Follow the work and record reviews. */
  track: boolean;
  /** Discard a detection (it leaves the counts and the score). */
  discard: boolean;
}

export const rightsOf = (me: Me | null | undefined): Rights => ({
  track: can(me, 'site', 'track'),
  discard: can(me, 'site', 'discard'),
});

const errorCode = (failure: unknown) => (failure instanceof ApiError ? failure.code : null);

/** Who did it and when, in one phrase; a person who has left the organisation is not named. */
function usePhrase() {
  const { t } = useTranslation();
  const date = useDate();
  return (by: Person | null, at: string) =>
    t('follow.by', { name: by?.name || t('follow.someone'), date: date(at, 'short') });
}

/** The link that opens the summary before an analysis, with the elements to check. */
export function CheckLink({
  siteId,
  ids,
  label,
  variant = 'secondary',
}: {
  siteId: string;
  ids: string[];
  label?: string;
  variant?: 'primary' | 'secondary';
}) {
  const { t } = useTranslation();
  if (ids.length === 0) return null;
  return (
    <Link
      to="/sites/$siteId"
      params={{ siteId }}
      search={{ preparar: true, comprobar: ids.join(',') }}
      className={buttonClass(variant)}
    >
      {label ?? t('follow.verify.action')}
    </Link>
  );
}

/** The three parts of an element's state, read-only and apart. */
export function StateReadout({ state }: { state: FollowState }) {
  const { t } = useTranslation();
  const date = useDate();
  const phrase = usePhrase();
  const validation = state.validation.status;
  return (
    <dl className="follow-readout">
      <div data-part="work">
        <dt>{t('follow.work.title')}</dt>
        <dd>
          <strong>{t(`follow.work.statuses.${state.work.status}`)}</strong>
          {state.work.at && <span className="text-ink-muted"> · {phrase(state.work.by, state.work.at)}</span>}
          {state.work.note && <span className="block italic">{state.work.note}</span>}
        </dd>
      </div>
      <div data-part="validation">
        <dt>{t('follow.validation.title')}</dt>
        <dd>
          <strong>
            {validation ? t(`follow.validation.statuses.${validation}`) : t('follow.validation.none')}
          </strong>
          {state.validation.at && (
            <span className="text-ink-muted"> · {phrase(state.validation.by, state.validation.at)}</span>
          )}
          {state.validation.justification && validation === 'descartada' && (
            <span className="block">
              {t('follow.validation.justificationLine', { text: state.validation.justification })}
            </span>
          )}
          {state.validation.evidence && validation === 'descartada' && (
            <span className="block">
              {t('follow.validation.evidenceLine', { text: state.validation.evidence })}
            </span>
          )}
        </dd>
      </div>
      <div data-part="check">
        <dt>{t('follow.check.title')}</dt>
        <dd>
          {state.check ? (
            <>
              <strong>{t(`follow.check.results.${state.check.result}`)}</strong>
              <span className="text-ink-muted">
                {' '}
                ·{' '}
                {t('follow.check.on', {
                  source: t(`follow.check.sources.${state.check.source}`),
                  date: date(state.check.at, 'short'),
                })}
                {state.check.source === 'manual' && state.check.by && <> · {state.check.by.name}</>}
              </span>
              <span className="block">{t(`follow.check.reasons.${state.check.reason}`)}</span>
              {state.check.note && <span className="block italic">{state.check.note}</span>}
              {state.check.devices.length > 0 && (
                <span className="block text-sm text-ink-muted">
                  {t('follow.check.devices', {
                    list: state.check.devices.map((device) => t(`scan.devices.${device}`)).join(', '),
                  })}
                </span>
              )}
              {state.work.status === 'aplicada' && state.check.result !== 'no-detectado' && (
                <span className="block text-sm">{t('follow.check.stillPending')}</span>
              )}
            </>
          ) : (
            <>
              <strong>{t('follow.check.none')}</strong>
              <span className="block text-sm text-ink-muted">{t('follow.check.noneHint')}</span>
            </>
          )}
        </dd>
      </div>
    </dl>
  );
}

const VALIDATION_CHOICES = {
  violation: ['confirmada', 'descartada'],
  incomplete: ['pendiente', 'confirmada', 'descartada'],
} as const;

/**
 * The form to record the work and the validation of an element (or of every element of a problem). The
 * discard of a detection asks for its justification and its evidence, and is for those who may discard.
 */
export function StateForm({
  scanId,
  siteId,
  target,
  state,
  result,
  count = 1,
  mayTrack,
  mayDiscard,
  heading,
  simple = false,
}: {
  scanId: string;
  siteId: string;
  target: StateTarget;
  /** The element's state; null when the form applies to every element of a problem. */
  state: FollowState | null;
  result: 'violation' | 'incomplete';
  /** How many elements it applies to. */
  count?: number;
  mayTrack: boolean;
  mayDiscard: boolean;
  heading?: string;
  /** The work is changed elsewhere (one control): this form is for the validation of the detection and the note. */
  simple?: boolean;
}) {
  const { t } = useTranslation();
  const refresh = useRefresh(scanId, siteId);
  const id = useId();
  const current = state?.work.status;
  const currentValidation = state?.validation.decided
    ? state.validation.status
    : result === 'incomplete'
      ? 'pendiente'
      : null;
  const [work, setWork] = useState<string>(current && current !== 'archivada' ? current : '');
  const [validation, setValidation] = useState<string>(currentValidation ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ ids: string[]; work: WorkStatus | null } | null>(null);
  const discarding = validation === 'descartada' && currentValidation !== 'descartada';

  // What the server says changes under the form (work reopened by a check, a review saved elsewhere): the
  // choices follow it. A save leaves them where the person put them, which is what the server now says.
  useEffect(() => setWork(current && current !== 'archivada' ? current : ''), [current]);
  useEffect(() => setValidation(currentValidation ?? ''), [currentValidation]);

  if (!mayTrack) return <p className="text-sm text-ink-muted">{t('follow.readOnly')}</p>;

  const validationOptions: ValidationStatus[] = [
    ...VALIDATION_CHOICES[result],
    ...((state?.validation.decided && result === 'violation' ? ['pendiente'] : []) as ValidationStatus[]),
  ].filter((value, index, all) => all.indexOf(value) === index);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { note = '', justification = '', evidence = '' } = readForm(event);
    setError(null);
    setSaved(null);
    const change: StateChange = {};
    const cleanNote = note.trim();
    if (work && (!state || work !== current || cleanNote !== (state.work.note ?? '')))
      change.work = work as Exclude<WorkStatus, 'archivada'>;
    if (cleanNote) change.note = cleanNote;
    if (validation && validation !== currentValidation) {
      change.validation = validation as ValidationStatus;
      if (validation === 'descartada') {
        change.justification = justification.trim();
        change.evidence = evidence.trim();
      }
    }
    if (!change.work && !change.validation) {
      setError(t('follow.errors.vacio'));
      return;
    }
    setBusy(true);
    try {
      const done = await changeState(scanId, target, change);
      await refresh();
      setSaved({
        ids: done.occurrences.map((item) => item.id).filter((value): value is string => Boolean(value)),
        work: change.work ?? null,
      });
      announce(count > 1 ? t('follow.savedMany', { count: done.updated }) : t('follow.saved'));
    } catch (failure) {
      const code = errorCode(failure);
      setError(
        code && code in (t('follow.errors', { returnObjects: true }) as Record<string, string>)
          ? t(`follow.errors.${code}`)
          : t('follow.errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Form onSubmit={submit} validationBehavior="native" className="follow-form">
      {heading && <p className="font-bold">{heading}</p>}
      {error && <Message tone="error">{error}</Message>}
      {!simple && (
        <Choice
          name={`work-${id}`}
          label={t('follow.work.title')}
          description={current === 'archivada' ? t('follow.work.reopens') : t('follow.work.description')}
          options={CHOOSABLE_WORK.map((value) => ({
            value,
            label: t(`follow.work.statuses.${value}`),
            hint: t(`follow.work.hints.${value}`),
          }))}
          value={work}
          onChange={setWork}
          isRequired={false}
        />
      )}
      <Choice
        name={`validation-${id}`}
        label={t('follow.validation.title')}
        description={t('follow.validation.description')}
        options={validationOptions.map((value) => ({
          value,
          label: t(`follow.validation.statuses.${value}`),
          hint:
            value === 'pendiente' && result === 'violation'
              ? t('follow.validation.hints.revert')
              : t(`follow.validation.hints.${value}`),
          disabled: value === 'descartada' && !mayDiscard,
        }))}
        value={validation}
        onChange={setValidation}
        isRequired={false}
      />
      {!mayDiscard && <p className="text-sm text-ink-muted">{t('follow.validation.adminOnly')}</p>}
      {discarding && (
        <div className="follow-discard">
          <p className="text-sm">{t('follow.validation.discardNote')}</p>
          <Field
            name="justification"
            multiline
            label={t('follow.validation.justification')}
            description={t('follow.validation.justificationHint')}
            minLength={20}
            maxLength={1000}
            isRequired
          />
          <Field
            name="evidence"
            multiline
            label={t('follow.validation.evidence')}
            description={t('follow.validation.evidenceHint')}
            minLength={10}
            maxLength={1000}
            isRequired
          />
        </div>
      )}
      {!simple && (
        <Field
          name="note"
          multiline
          label={t('follow.noteLabel')}
          description={t('follow.noteHint')}
          defaultValue={state?.work.note ?? ''}
          maxLength={1000}
        />
      )}
      {!simple && <p className="follow-notice">{t('follow.notice')}</p>}
      <div className="flex flex-wrap items-center gap-4">
        <Button type="submit" variant="secondary" isPending={busy}>
          {busy ? t('follow.saving') : t('follow.save')}
        </Button>
        {count > 1 && (
          <span className="text-sm text-ink-muted">{t('follow.problem.bulkApplies', { count })}</span>
        )}
      </div>
      <div role="status" className="follow-saved">
        {saved && (
          <>
            <p>
              <CheckCircledIcon aria-hidden="true" />{' '}
              {count > 1 ? t('follow.savedMany', { count: saved.ids.length || count }) : t('follow.saved')}
            </p>
            {saved.ids.length > 0 && saved.work !== null && saved.work !== 'pendiente' && (
              <>
                <p className="text-sm text-ink-muted">{t('follow.verify.hint')}</p>
                <CheckLink siteId={siteId} ids={saved.ids} />
              </>
            )}
          </>
        )}
      </div>
    </Form>
  );
}

/** A manual review of an element: when the analysis cannot decide, or a person prefers to check it. */
function ManualReview({
  scanId,
  siteId,
  element,
  devices,
  mayTrack,
  steps,
}: {
  scanId: string;
  siteId: string;
  element: ElementRef;
  devices: Device[];
  mayTrack: boolean;
  /** The steps of the guide to check the element by hand. */
  steps?: string[];
}) {
  const { t } = useTranslation();
  const refresh = useRefresh(scanId, siteId);
  const [outcome, setOutcome] = useState<'favorable' | 'desfavorable'>('favorable');
  const [chosen, setChosen] = useState<Device[]>(devices);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  if (!mayTrack) return null;

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { note = '' } = readForm(event);
    setError(null);
    setDone(false);
    setBusy(true);
    try {
      await reviewElement(element, { outcome, devices: chosen, note: note.trim() });
      await refresh();
      setDone(true);
      announce(t('follow.review.saved'));
    } catch (failure) {
      const code = errorCode(failure);
      setError(
        code === 'ambito' || code === 'archivada' || code === 'permiso'
          ? t(`follow.review.errors.${code}`)
          : t('follow.review.errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Fold title={t('follow.review.title')} light>
      <Form onSubmit={submit} validationBehavior="native" className="follow-form">
        <p className="text-sm text-ink-muted">{t('follow.review.hint')}</p>
        {steps && steps.length > 0 && (
          <ol className="guide-steps">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        )}
        {error && <Message tone="error">{error}</Message>}
        <Choice
          label={t('follow.review.outcome')}
          options={[
            {
              value: 'favorable',
              label: t('follow.review.favorable'),
              hint: t('follow.review.favorableHint'),
            },
            {
              value: 'desfavorable',
              label: t('follow.review.desfavorable'),
              hint: t('follow.review.desfavorableHint'),
            },
          ]}
          value={outcome}
          onChange={setOutcome}
        />
        <fieldset className="follow-devices">
          <legend className="font-bold">{t('follow.review.devices')}</legend>
          <p className="text-sm text-ink-muted">{t('follow.review.devicesHint')}</p>
          {(['escritorio', 'movil'] as const).map((device) => (
            <label key={device} className="follow-check">
              <input
                type="checkbox"
                checked={chosen.includes(device)}
                onChange={(event) =>
                  setChosen((list) =>
                    event.target.checked ? [...list, device] : list.filter((item) => item !== device),
                  )
                }
              />
              {t(`scan.devices.${device}`)}
            </label>
          ))}
        </fieldset>
        <Field
          name="note"
          multiline
          label={t('follow.review.note')}
          description={t('follow.review.noteHint')}
          minLength={10}
          maxLength={1000}
          isRequired
        />
        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" variant="secondary" isPending={busy} isDisabled={chosen.length === 0}>
            {t('follow.review.save')}
          </Button>
        </div>
        <div role="status">{done ? t('follow.review.saved') : ''}</div>
      </Form>
    </Fold>
  );
}

/** The evidence behind an archive or a possible archive: which check, when and how. */
function EvidenceLine({
  evidence,
}: {
  evidence: { source: 'automatica' | 'manual'; reason: CheckReason; at: string };
}) {
  const { t } = useTranslation();
  const date = useDate();
  return (
    <p>
      {t(evidence.source === 'manual' ? 'follow.archive.evidenceManual' : 'follow.archive.evidenceAuto', {
        date: date(evidence.at, 'short'),
      })}
    </p>
  );
}

/** Archiving the work, only with the evidence of a check; or why it cannot be archived yet. */
export function ArchiveBox({
  scanId,
  siteId,
  state,
  mayTrack,
}: {
  scanId: string;
  siteId: string;
  state: FollowState;
  mayTrack: boolean;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const refresh = useRefresh(scanId, siteId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  if (!state.id) return null;
  const id = state.id;
  if (state.archive) {
    return (
      <div className="follow-archive">
        <p>
          <strong>
            {t('follow.archive.archivedBy', {
              date: date(state.archive.at, 'short'),
              name: state.archive.by?.name || t('follow.someone'),
            })}
          </strong>
        </p>
        {state.archive.evidence && <EvidenceLine evidence={state.archive.evidence} />}
        <p className="text-sm text-ink-muted">{t('follow.archive.notCertification')}</p>
      </div>
    );
  }

  async function archive(event: FormEvent<HTMLFormElement>) {
    const { note = '' } = readForm(event);
    setError(null);
    setBusy(true);
    try {
      await archiveWork(id, note);
      await refresh();
      setDone(true);
      announce(t('follow.archive.done'));
    } catch (failure) {
      const code = errorCode(failure);
      setError(
        code === 'sin-evidencia' || code === 'permiso'
          ? t(`follow.archive.errors.${code}`)
          : t('follow.archive.errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="follow-archive">
      <p className="font-bold">{t('follow.archive.title')}</p>
      {state.canArchive.ok ? (
        <>
          <p className="font-bold">{t('follow.archive.evidence')}</p>
          <EvidenceLine evidence={state.canArchive.evidence} />
          <p className="text-sm text-ink-muted">{t('follow.archive.notCertification')}</p>
          {mayTrack && (
            <Form onSubmit={archive} validationBehavior="native" className="follow-form">
              {error && <Message tone="error">{error}</Message>}
              <Field
                name="note"
                multiline
                label={t('follow.archive.noteLabel')}
                description={t('follow.archive.noteHint')}
                maxLength={1000}
              />
              <div className="flex flex-wrap items-center gap-4">
                <Button type="submit" isPending={busy}>
                  {busy ? t('follow.archive.archiving') : t('follow.archive.action')}
                </Button>
              </div>
              <div role="status">{done ? t('follow.archive.done') : ''}</div>
            </Form>
          )}
        </>
      ) : (
        <p className="text-sm text-ink-muted">{t(`follow.archive.why.${state.canArchive.why}`)}</p>
      )}
    </div>
  );
}

/** One line of the history, in words. */
function EventLine({ event }: { event: FollowEvent }) {
  const { t } = useTranslation();
  const date = useDate();
  // What each kind of line carries (see plans in apps/api/src/follow-up.ts).
  const detail = event.detail as {
    migrated?: boolean;
    manual?: boolean;
    from?: unknown;
    to?: unknown;
    note?: string;
    justification?: string;
    evidence?: string;
    result?: string;
    reason?: string;
    outcome?: string;
    guided?: boolean;
    criterion?: string;
    devices?: string[];
    lifted?: { justification?: string | null; evidence?: string | null };
  };
  const work = (value: unknown) =>
    t(`follow.work.statuses.${String(value)}`, { defaultValue: String(value) });
  const validation = (value: unknown) =>
    value ? t(`follow.validation.statuses.${String(value)}`) : t('follow.history.none');
  let text: string;
  switch (event.kind) {
    case 'creada':
      text = detail.migrated
        ? t('follow.history.createdMigrated', { from: String(detail.from) })
        : t('follow.history.created');
      break;
    case 'seguimiento':
      text = t('follow.history.work', { from: work(detail.from), to: work(detail.to) });
      break;
    case 'validacion':
      text = t('follow.history.validation', { from: validation(detail.from), to: validation(detail.to) });
      break;
    case 'comprobacion':
      text = t('follow.history.check', {
        result: t(`follow.check.results.${detail.result}`),
        reason: t(`follow.check.reasons.${detail.reason}`),
      });
      break;
    case 'revision-manual':
      text = detail.guided
        ? t('follow.history.guided', {
            criterion: detail.criterion ?? '',
            outcome: t(
              `manual.answer.${detail.outcome === 'favorable' ? 'sin-problemas' : detail.outcome === 'desfavorable' ? 'problema' : detail.outcome}`,
            ),
            devices: (detail.devices ?? []).map((device) => t(`scan.devices.${device}`)).join(', '),
            text: String(detail.note ?? ''),
          })
        : t('follow.history.review', {
            outcome: t(
              detail.outcome === 'favorable' ? 'follow.review.favorable' : 'follow.review.desfavorable',
            ),
            devices: (detail.devices ?? []).map((device) => t(`scan.devices.${device}`)).join(', '),
            text: String(detail.note ?? ''),
          });
      break;
    case 'archivo':
      text = t('follow.history.archive');
      break;
    default:
      text = detail.manual ? t('follow.history.reopenManual') : t('follow.history.reopenAuto');
  }
  const note =
    event.kind === 'seguimiento' && detail.note
      ? t('follow.history.note', { text: String(detail.note) })
      : event.kind === 'validacion' && detail.to === 'descartada'
        ? [
            detail.justification &&
              t('follow.validation.justificationLine', { text: String(detail.justification) }),
            detail.evidence && t('follow.validation.evidenceLine', { text: String(detail.evidence) }),
          ]
            .filter(Boolean)
            .join(' ')
        : event.kind === 'archivo' && detail.note
          ? t('follow.history.note', { text: String(detail.note) })
          : '';
  const lifted =
    event.kind === 'validacion' && detail.lifted
      ? t('follow.history.lifted', {
          justification: String(detail.lifted.justification ?? ''),
          evidence: String(detail.lifted.evidence ?? ''),
        })
      : '';
  return (
    <li>
      <span className="font-bold">
        {t('follow.history.actor', {
          name:
            event.by?.name ||
            (event.kind === 'comprobacion' || event.kind === 'reapertura'
              ? t('follow.system')
              : t('follow.someone')),
          date: date(event.at, 'long'),
        })}
      </span>
      <span className="block">{text}</span>
      {note && <span className="block text-sm">{note}</span>}
      {lifted && <span className="block text-sm">{lifted}</span>}
    </li>
  );
}

/** Everything that happened to an element, oldest first. Loaded when it is opened. */
function History({ occurrenceId }: { occurrenceId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const history = useQuery({ ...historyQuery(occurrenceId), enabled: open });
  return (
    <Fold title={t('follow.history.title')} light onToggle={setOpen}>
      {open &&
        (history.isPending ? (
          <p role="status">{t('follow.history.loading')}</p>
        ) : history.isError ? (
          <p role="alert">{t('follow.history.error')}</p>
        ) : history.data.length === 0 ? (
          <p>{t('follow.history.empty')}</p>
        ) : (
          <ol className="follow-events">
            {history.data.map((event) => (
              <EventLine key={event.id} event={event} />
            ))}
          </ol>
        ))}
    </Fold>
  );
}

/**
 * The work of an element: where the correction stands for the team, changed with one control that saves at once. It
 * is the team's word and says nothing about whether the problem is solved.
 */
function WorkBlock({
  scanId,
  siteId,
  element,
  state,
  mayTrack,
}: {
  scanId: string;
  siteId: string;
  element: ElementRef;
  state: FollowState;
  mayTrack: boolean;
}) {
  const { t } = useTranslation();
  const refresh = useRefresh(scanId, siteId);
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const current = state.work.status;
  const archived = current === 'archivada';

  async function change(work: Exclude<WorkStatus, 'archivada'>) {
    if (work === current) return;
    setError(null);
    setSaved(false);
    setBusy(true);
    try {
      await changeState(
        scanId,
        'row' in element ? { row: element.row } : { occurrenceIds: [element.occurrenceId] },
        // The note of the work is kept: changing the status is not rewriting it.
        { work, ...(state.work.note ? { note: state.work.note } : {}) },
      );
      await refresh();
      setSaved(true);
      announce(t('follow.saved'));
    } catch (failure) {
      const code = errorCode(failure);
      setError(
        code && code in (t('follow.errors', { returnObjects: true }) as Record<string, string>)
          ? t(`follow.errors.${code}`)
          : t('follow.errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="follow-block" data-part="work">
      <label htmlFor={id} className="follow-block-title">
        {t('follow.quick.work')}
      </label>
      {error && <Message tone="error">{error}</Message>}
      <div className="follow-block-row">
        {mayTrack ? (
          <select
            id={id}
            value={archived ? 'archivada' : current}
            disabled={busy}
            onChange={(event) => void change(event.target.value as Exclude<WorkStatus, 'archivada'>)}
            className="follow-select"
          >
            {archived && (
              <option value="archivada" disabled>
                {t('follow.work.statuses.archivada')}
              </option>
            )}
            {CHOOSABLE_WORK.map((value) => (
              <option key={value} value={value}>
                {t(`follow.work.statuses.${value}`)}
              </option>
            ))}
          </select>
        ) : (
          <strong>{t(`follow.work.statuses.${current}`)}</strong>
        )}
        <span role="status" className="follow-quick-saved">
          {saved ? t('follow.saved') : ''}
        </span>
      </div>
    </section>
  );
}

/**
 * What a check found, apart from the work: not checked, pending, verified or still detected, and whether it comes from an
 * automatic analysis or a manual check. A declared correction stays pending until a check shows it is gone; when the
 * automatic analysis cannot decide, it says so and leads to the manual check.
 */
function VerificationBlock({
  siteId,
  state,
  guideTitles,
  pageKey,
}: {
  siteId: string;
  state: FollowState;
  guideTitles?: Record<string, string>;
  pageKey?: string;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const verification = verificationOf(state);
  const { kind } = verification;
  const checkable = Boolean(state.id) && state.work.status !== 'archivada' && kind !== 'descartada';
  const open = kind === 'pendiente' || kind === 'persiste' || kind === 'sin-comprobar';
  const hint =
    kind === 'pendiente' && !verification.needsManual
      ? t('follow.panel.hints.pendiente')
      : kind === 'persiste'
        ? t('follow.panel.hints.persiste')
        : kind === 'descartada'
          ? t('follow.panel.hints.descartada')
          : null;
  return (
    <section className="follow-block" data-part="verification" data-kind={kind}>
      <h5 className="follow-block-title">{t('follow.quick.verification')}</h5>
      <p className="follow-verification">
        {kind === 'verificado' ? (
          <CheckCircledIcon aria-hidden="true" />
        ) : kind === 'persiste' ? (
          <ExclamationTriangleIcon aria-hidden="true" />
        ) : (
          <ClockIcon aria-hidden="true" />
        )}
        <strong>{t(`follow.panel.kinds.${kind}`)}</strong>
        {verification.source && verification.at && (
          <span className="text-ink-muted">
            {t('follow.panel.on', {
              source: t(`follow.panel.sources.${verification.source}`),
              date: date(verification.at, 'short'),
            })}
          </span>
        )}
      </p>
      {hint && <p className="text-sm text-ink-muted">{hint}</p>}
      {verification.needsManual && kind !== 'verificado' && kind !== 'descartada' && (
        <p className="text-sm">{t('follow.panel.manual')}</p>
      )}
      {open && (
        <div className="follow-block-actions">
          {verification.needsManual &&
            state.manual.guides.map((criterion) => (
              <GuideLink
                key={criterion}
                siteId={siteId}
                criterion={criterion}
                variant="primary"
                search={{
                  ...(state.id ? { occurrence: state.id } : {}),
                  ...(pageKey ? { p: pageKey } : {}),
                }}
              >
                {guideTitles?.[criterion]
                  ? t('manual.follow.repeat', { title: guideTitles[criterion] })
                  : t('manual.follow.repeatId', { id: criterion })}
              </GuideLink>
            ))}
          {checkable && state.id && (
            <CheckLink
              siteId={siteId}
              ids={[state.id]}
              label={kind === 'sin-comprobar' ? t('follow.panel.check') : t('follow.panel.recheck')}
              variant={
                verification.needsManual ? 'secondary' : kind === 'sin-comprobar' ? 'secondary' : 'primary'
              }
            />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Applying a work status to several elements at once: pick the status, say how many elements it reaches and confirm. It
 * changes the work only: verification results are never modified, and each element keeps its own history.
 */
export function BulkState({
  scanId,
  siteId,
  target,
  count,
  mayTrack,
}: {
  scanId: string;
  siteId: string;
  /** The elements it applies to: some of a page, or every element of some problems. */
  target: StateTarget;
  /** How many elements that is. */
  count: number;
  mayTrack: boolean;
}) {
  const { t } = useTranslation();
  const refresh = useRefresh(scanId, siteId);
  const id = useId();
  const [work, setWork] = useState<string>('');
  const [done, setDone] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!mayTrack || count < 2) return null;

  async function apply() {
    setError(null);
    setDone(null);
    try {
      const saved = await changeState(scanId, target, {
        work: work as Exclude<WorkStatus, 'archivada'>,
      });
      await refresh();
      setDone(saved.updated);
      announce(t('follow.panel.bulk.done', { count: saved.updated }));
    } catch (failure) {
      const code = errorCode(failure);
      setError(
        code && code in (t('follow.errors', { returnObjects: true }) as Record<string, string>)
          ? t(`follow.errors.${code}`)
          : t('follow.errors.generic'),
      );
    }
  }

  return (
    <Fold title={t('follow.panel.bulk.title', { count })} light>
      <div className="follow-bulk">
        <label htmlFor={id} className="text-sm">
          {t('follow.panel.bulk.hint')}
        </label>
        {error && <Message tone="error">{error}</Message>}
        <div className="follow-block-row">
          <select
            id={id}
            value={work}
            onChange={(event) => {
              setWork(event.target.value);
              setDone(null);
            }}
            className="follow-select"
          >
            <option value="">{t('follow.panel.bulk.choose')}</option>
            {CHOOSABLE_WORK.map((value) => (
              <option key={value} value={value}>
                {t(`follow.work.statuses.${value}`)}
              </option>
            ))}
          </select>
          <ConfirmButton
            variant="primary"
            isDisabled={!work}
            label={t('follow.panel.bulk.apply')}
            hidden={t('follow.panel.bulk.title', { count })}
            title={t('follow.panel.bulk.confirmTitle', { count })}
            body={t('follow.panel.bulk.confirmBody', { count })}
            confirm={t('follow.panel.bulk.confirm')}
            cancel={t('follow.panel.bulk.cancel')}
            onConfirm={apply}
          />
        </div>
        <p role="status" className="follow-quick-saved">
          {done !== null ? t('follow.panel.bulk.done', { count: done }) : ''}
        </p>
      </div>
    </Fold>
  );
}

/**
 * «Estado de la incidencia» of one element: its three states side by side and apart, the form to change
 * them, the way to check the fix, the archive when a check allows it and the history.
 */
export function ElementState({
  scanId,
  siteId,
  element,
  state,
  result,
  devices,
  mayTrack,
  mayDiscard,
  checkSteps,
  headingLevel = 5,
  inList = false,
  hideTitle = false,
  pageKey,
  guideTitles,
  extra,
}: {
  /** The analysis the page is read from (what is read again after a change). */
  scanId: string;
  siteId: string;
  /** The element: as the scan lists it, or by its own id when the team follows it already. */
  element: ElementRef;
  state: FollowState;
  result: 'violation' | 'incomplete';
  devices: Device[];
  mayTrack: boolean;
  mayDiscard: boolean;
  checkSteps?: string[];
  headingLevel?: 5 | 6;
  /** Inside the follow-up list, which already shows the three states: they are not repeated. */
  inList?: boolean;
  /** The title is said by whoever folds this away (a summary): it is not repeated. */
  hideTitle?: boolean;
  /** The page of the element: the guide opens on it. */
  pageKey?: string;
  /** The names of the criteria whose guides can repeat the check, by id. */
  guideTitles?: Record<string, string>;
  /** Something that belongs with the advanced options (applying one state to every element of the problem). */
  extra?: ReactNode;
}) {
  const { t } = useTranslation();
  const Heading = `h${headingLevel}` as const;
  const checkable = state.id && state.work.status !== 'archivada' && state.validation.status !== 'descartada';
  const target = 'row' in element ? { row: element.row } : { occurrenceIds: [element.occurrenceId] };
  const stateKey = 'row' in element ? element.row : element.occurrenceId;
  const guides =
    state.manual.guides.length > 0 ? (
      <Fold title={t('manual.follow.title')} light>
        <div className="follow-verify">
          <p className="text-sm text-ink-muted">{t('manual.follow.hint')}</p>
          <div className="flex flex-wrap gap-3">
            {state.manual.guides.map((id) => (
              <GuideLink
                key={id}
                siteId={siteId}
                criterion={id}
                variant="secondary"
                search={{
                  ...(state.id ? { occurrence: state.id } : {}),
                  ...(pageKey ? { p: pageKey } : {}),
                }}
              >
                {guideTitles?.[id]
                  ? t('manual.follow.repeat', { title: guideTitles[id] })
                  : t('manual.follow.repeatId', { id })}
              </GuideLink>
            ))}
          </div>
        </div>
      </Fold>
    ) : null;
  // In the follow-up list, which already shows the three states, the whole form is at hand.
  if (inList) {
    return (
      <div className="follow-state">
        {checkable && state.id && (
          <div className="follow-verify">
            <p className="text-sm text-ink-muted">{t('follow.verify.hint')}</p>
            <CheckLink
              siteId={siteId}
              ids={[state.id]}
              variant={state.work.status === 'aplicada' ? 'primary' : 'secondary'}
            />
          </div>
        )}
        {guides}
        <StateForm
          key={stateKey}
          scanId={scanId}
          siteId={siteId}
          target={target}
          state={state}
          result={result}
          mayTrack={mayTrack}
          mayDiscard={mayDiscard}
        />
        {state.id && state.work.status !== 'archivada' && (
          <ManualReview
            scanId={scanId}
            siteId={siteId}
            element={element}
            devices={devices}
            mayTrack={mayTrack}
            steps={checkSteps}
          />
        )}
        {state.id && <History occurrenceId={state.id} />}
      </div>
    );
  }
  // The work (the team's word) and the verification (what a check found) apart; everything else is one fold away.
  const verification = verificationOf(state);
  return (
    <div className="follow-state">
      {!hideTitle && <Heading>{t('follow.title')}</Heading>}
      <p className="follow-intro">{t('follow.panel.intro')}</p>
      <WorkBlock
        key={`work-${stateKey}`}
        scanId={scanId}
        siteId={siteId}
        element={element}
        state={state}
        mayTrack={mayTrack}
      />
      <VerificationBlock siteId={siteId} state={state} guideTitles={guideTitles} pageKey={pageKey} />
      <Fold title={t('follow.panel.advanced')} hint={t('follow.panel.advancedHint')}>
        <div className="follow-more-body">
          {!verification.needsManual && guides}
          <StateForm
            key={stateKey}
            scanId={scanId}
            siteId={siteId}
            target={target}
            state={state}
            result={result}
            mayTrack={mayTrack}
            mayDiscard={mayDiscard}
            simple
          />
          {state.id && state.work.status !== 'archivada' && (
            <ManualReview
              scanId={scanId}
              siteId={siteId}
              element={element}
              devices={devices}
              mayTrack={mayTrack}
              steps={checkSteps}
            />
          )}
          {(state.canArchive.ok || state.archive) && (
            <ArchiveBox scanId={scanId} siteId={siteId} state={state} mayTrack={mayTrack} />
          )}
          {state.id && <History occurrenceId={state.id} />}
          {extra}
        </div>
      </Fold>
    </div>
  );
}
